import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RawJSON } from "../../../json";
import { createEnum } from "../../../utils/utils";
import {
  Attachment,
  useAddressAttachmentType,
  useFaceAttachmentType,
  useIdentityAttachmentType,
} from "../../common/models";
import { StatusEnum } from "../../users/models/internal";

export const ServiceType = createEnum({
  ADDRESS: "Address",
  BACKGROUND_CHECKS: "Background Checks",
  CONSENT: "Consent",
  FACE: "Face",
  DOCUMENT: "Identity",
});

export const CertificateDetails = createEnum({
  NATIONALITY: "Nationality",
  DOB: "DOB",
  ADDRESS: "Address",
  COUNTRY: "Country",
});

export const DeclineCode = createEnum({
  FACE_NOT_VERIFIED: "Face Not Verified",
  FACE_NOT_IN_IMAGE: "Face Not In Image",
  IMAGE_ALTERED: "Image Altered",
  IMAGE_FOUND_WEB: "Image Found Web",
  FACE_WEARING_GLASSES: "Face Wearing Glasses",
  FACE_FROM_OTHER_SCREEN: "Face From Other Screen",
  FACE_TAKEN_FROM_WEB: "Face Taken From Web",
  MULTIPLE_FACES: "Multiple Faces",
  FACE_SCREENSHOT: "Face Screenshot",
  FACE_IMAGE_NOT_MATCHED: "Face Image Not Matched",
  DOCUMENT_NOT_MATCHED: "Document Not Matched",
  CAMERA_NOT_ACCESSIBLE: "Camera Not Accessible",
  FACE_NOT_FOUND: "Face Not Found",
  DOCUMENT_ORIGIN_NOT_VERIFIED: "Document Origin Not Verified",
  NAME_NOT_MATCHED: "Name Not Matched",
  DOB_NOT_MATCHED: "Dob Not Matched",
  EXPIRY_DATE_NOT_MATCHED: "Expiry Date Not Matched",
  ISSUE_DATE_NOT_MATCHED: "Issue Date Not Matched",
  DATE_NOT_MATCHED: "Date Not Matched",
  NUMBER_NOT_MATCHED: "Number Not Matched",
  ISSUE_COUNTRY_NOT_SUPPORTED: "Issue Country Not Supported",
  DOCUMENT_OPTIONS_NOT_MATCHED: "Document Options Not Matched",
  AGE_NOT_IN_RANGE: "Age Not In Range",
  FACE_CAMERA_NOT_MATCHED: "Face Camera Not Matched",
  DOCUMENT_EXPIRED: "Document Expired",
  BLURRED_DOCUMENT: "Blurred Document",
  PROOFS_DIFFERENT_DOCS: "Proofs Different Docs",
  FRONT_BACK_NOT_MATCHED: "Front Back Not Matched",
  PROOF_SCREENSHOT: "Proof Screenshot",
  PROOF_ALTERED: "Proof Altered",
  PROOF_PAPER_BASED: "Proof Paper Based",
  PROOF_BROKEN: "Proof Broken",
  PROOF_OTHER_SCREEN: "Proof Other Screen",
  HOLOGRAM_MISSING: "Hologram Missing",
  PROOF_NOT_FULLY_DISPLAYED: "Proof Not Fully Displayed",
  PROOF_INFO_NOT_VISIBLE: "Proof Info Not Visible",
  DOCUMENT_INFO_EDITED: "Document Info Edited",
  DOCUMENT_INFO_HIDDEN: "Document Info Hidden",
  DOCUMENT_NOT_PROVIDED_COUNTRY: "Document Not Provided Country",
  ISSUE_DATE_NOT_VISIBLE: "Issue Date Not Visible",
  EXPIRY_DATE_NOT_VISIBLE: "Expiry Date Not Visible",
  DOB_NOT_VISIBLE: "Dob Not Visible",
  NAME_NOT_VISIBLE: "Name Not Visible",
  NUMBER_NOT_VISIBLE: "Number Not Visible",
  NUMBER_NOT_AUTHENTICATED: "Number Not Authenticated",
  PROOF_NOT_SAME_PERSON: "Proof Not Same Person",
  ADDRESS_DOCUMENT_NOT_MATCHED: "Address Document Not Matched",
  GENDER_NOT_VERIFIED: "Gender Not Verified",
  ISSUE_PLACE_NOT_VERIFIED: "Issue Place Not Verified",
  DOCUMENTS_NAME_NOT_MATCHED: "Documents Name Not Matched",
  ADDRESS_NAME_NOT_MATCHED: "Address Name Not Matched",
  ADDRESS_NOT_MATCHED: "Address Not Matched",
  DOCUMENT_TYPE_NOT_ACCEPTED: "Document Type Not Accepted",
  ADDRESS_COUNTRY_NOT_VERIFIED: "Address Country Not Verified",
  ADDRESS_DOCUMENT_BILL_NOT_MATCHED: "Address Document Bill Not Matched",
  ADDRESS_INVALID: "Address Invalid",
  ADDRESS_NOT_VISIBLE: "Address Not Visible",
  ADDRESS_NOT_VALIDATED: "Address Not Validated",
  SAME_ID_SUBMITTED: "Same Id Submitted",
  CONSENT_NOTE_INCORRECT: "Consent Note Incorrect",
  CONSENT_TYPE_NOT_ACCEPTED: "Consent Type Not Accepted",
  AML_FAILED: "Aml Failed",
  USER_SANCTIONED: "User Sanctioned",
  USER_WARNING: "User Warning",
  USER_FITNESS_PROBITY: "User Fitness Probity",
  USER_PEP: "User Pep",
  USER_ADVERSE_MEDIA: "User Adverse Media",
  BUSINESS_SANCTIONED: "Business Sanctioned",
  BUSINESS_WARNING: "Business Warning",
  BUSINESS_FITNESS_PROBITY: "Business Fitness Probity",
  BUSINESS_PEP: "Business Pep",
  BUSINESS_ADVERSE_MEDIA: "Business Adverse Media",
  PHONE_NUMBER_NOT_MATCHED: "Phone Number Not Matched",
  PHONE_NUMBER_NOT_VERIFIED: "Phone Number Not Verified",
});

export type DueDiligenceKycType = ReturnType<typeof useDueDiligenceKycType>;

export const useDueDiligenceKycType = () => {
  const { t } = useTranslation("common");

  return useMemo(
    () =>
      createEnum({
        UBO: t("ultimate-beneficial-owner"),
        SHAREHOLDER: t("shareholder"),
        COMPANY_MEMBER: t("company-member"),
      }),
    [t]
  );
};

export type FileType =
  | ReturnType<typeof useFaceAttachmentType>
  | ReturnType<typeof useIdentityAttachmentType>
  | ReturnType<typeof useAddressAttachmentType>;

export type FaceFileCategory = ReturnType<typeof useFaceFileCategory>;
export const useFaceFileCategory = () => {
  const { t } = useTranslation("common");

  return useMemo(
    () =>
      createEnum({
        FACE: t("face"),
      }),
    [t]
  );
};

export type AddressFileCategory = ReturnType<typeof useAddressFileCategory>;
export const useAddressFileCategory = () => {
  const { t } = useTranslation("common");

  return useMemo(
    () =>
      createEnum({
        ADDRESS: t("address"),
      }),
    [t]
  );
};

export type IdentityFileCategory = ReturnType<typeof useIdentityFileCategory>;
export const useIdentityFileCategory = () => {
  const { t } = useTranslation("common");

  return useMemo(
    () =>
      createEnum({
        IDENTITY: t("identity"),
        ADDITIONAL_IDENTITY: t("additional-identity"),
      }),
    [t]
  );
};

export type FileCategory =
  | FaceFileCategory
  | IdentityFileCategory
  | AddressFileCategory;

export interface DeclineCode {
  code: typeof DeclineCode.T;
  message: string;
}

export interface ServiceDetails {
  status: Exclude<StatusEnum, "DELETED">;
  declineCodes: Array<DeclineCode>;
  documents: Array<Attachment>;
}

export interface Service {
  service: typeof ServiceType.T;
  details: ServiceDetails;
}

export interface VerificationData {
  kyb: {
    companyNumber: null | string;
    companyType: null | string;
    companySource: {
      publisher: string | null;
      url: string | null;
      terms: null | string;
      retrievedAt: Date | null;
    } | null;
    nativeCompanyNumber: null | string;
    companyIndustryCodes:
      | [
          {
            code: string | null;
            description: string | null;
            codeSchemeId: string | null;
            codeSchemeName: string | null;
          }
        ]
      | null;
    companyTrademarkRegistrations:
      | [
          {
            registerName: string | null;
            markDetails: string | null;
            registrationDate: Date | null;
            expiryDate: Date | null;
          }
        ]
      | null;
    companyCorporateGroupings:
      | [
          {
            name: string | null;
            wikipediaId: string | null;
            updatedAt: string | null;
            referenceUrl: string | null;
          }
        ]
      | null;
    companyData:
      | [
          {
            title: string | null;
            dataType: string | null;
            description: string | null;
          }
        ]
      | null;
    homeCompany: string | null;
    companyUltimateBeneficialOwners:
      | [
          {
            name: string | null;
          }
        ]
      | null;
    companyFilings:
      | [
          {
            title: string | null;
            date: Date | null;
            referenceUrl: string | null;
          }
        ]
      | null;

    companyOfficers:
      | [
          {
            name: string | null;
            position: string | null;
            occupation: string | null;
            address: string | null;
            currentStatus: string | null;
            inactive: boolean | null;
            startDate: Date | null;
            endDate: Date | null;
          }
        ]
      | null;

    companyName: string;
    companyJurisdictionCode: string;
    companyIncorporationDate: Date;
    companyDissolutionDate: Date | null;
    companyRegistryUrl: string | null;
    companyBranch: string | null;
    companyBranchStatus: string | null;
    companyRegisteredAddressInFull: string | null;
    companyInactive: boolean | null;
    companyCurrentStatus: string | null;
    companyAgentName: string | null;
    companyAgentAddress: string | null;
    companyAlternativeNames:
      | [
          {
            companyName: string | null;
            type: string | null;
          }
        ]
      | null;
    companyPreviousNames: [
      {
        companyName: string | null;
        startDate: Date | null;
        endDate: Date | null;
      }
    ];
    companyNumberOfEmployees: string | null;
    companyAlternateRegistrationEntities: Array<RawJSON> | null;
    companyPreviousRegistrationEntities: Array<RawJSON> | null;
    companySubsequentRegistrationEntities: Array<RawJSON> | null;
    merchantIdentifiers: Array<{
      uid: string | null;
      identifierSystemCode: string | null;
      identifierSystemName: string | null;
    }> | null;

    companyRegisteredAddress: {
      streetAddress: string | null;
      locality: string | null;
      region: string | null;
      postalCode: string | null;
      country: string | null;
    } | null;
    companyFinancialSummary: Array<{
      sourceUrl: string | null;
      profit: string | null;
      revenue: string | null;
      currency: string | null;
      originalSource: string | null;
      accountsType: string | null;
      currentLiabilities: string | null;
      accountsDate: Date | null;
      fixedAssets: Array<KYBAssets> | null;
      currentAssets: Array<KYBAssets> | null;
      cashAtBank: Array<KYBAssets> | null;
    }> | null;
    companyControllingEntity: {
      name: string | null;
      jurisdictionCode: string | null;
      companyNumber: string | null;
      referenceUrl: string | null;
    };
  };
}

interface KYBAssets {
  value: string | null;
  date: Date | null;
}

export const DueDiligenceFormStatus = createEnum({
  PENDING: "Pending",
  SUBMITTED: "Submitted",
  PROCESSED: "Processed",
  VERIFIED: "Verified",
  DECLINED: "Declined",
});

export const DueDiligenceFileCategory = createEnum({
  INCUBATION_OR_INCORPORATION: "Incubation or incorporation",
  COMPANY_STRUCTURE_CHART: "Company structure chart",
  IDENTIFICATION_DOCUMENT: "Identification document",
  PROOF_OF_ADDRESS: "Proof of address",
  FACE: "Face",
  ADDITIONAL_IDENTIFICATION_DOCUMENT: "Additional identification document",
});
